import React, { useState, useMemo, useRef } from 'react';

function generateId() {
  return Math.random().toString(36).slice(2, 10);
}

function fmtEur(n) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n || 0);
}

const TAX_OPTIONS = [
  { value: 0,    label: 'Keine MwSt.' },
  { value: 0.19, label: '19% MwSt.' },
  { value: 0.07, label: '7% MwSt.' },
];

const QUOTE_STATUSES = [
  { key: 'entwurf',    label: 'Entwurf',    color: '#66796c', bg: 'rgba(102,121,108,0.1)' },
  { key: 'gesendet',   label: 'Gesendet',   color: '#159c5f', bg: 'rgba(29,190,116,0.1)' },
  { key: 'angenommen', label: 'Angenommen', color: '#16a34a', bg: 'rgba(22,163,74,0.1)'  },
  { key: 'abgelehnt',  label: 'Abgelehnt',  color: '#ef4444', bg: 'rgba(239,68,68,0.1)'  },
];

function loadQuotes(leadId) {
  try { return JSON.parse(localStorage.getItem(`crm_quotes_${leadId}`)) || []; } catch { return []; }
}
function saveQuotes(leadId, quotes) {
  try { localStorage.setItem(`crm_quotes_${leadId}`, JSON.stringify(quotes)); } catch {}
}

function newItem() {
  return { id: generateId(), description: '', qty: 1, unit: 'Pauschal', unitPrice: 0 };
}

function QuotePrint({ quote, lead, company }) {
  const net  = quote.items.reduce((s, i) => s + i.qty * i.unitPrice, 0);
  const tax  = net * (quote.taxRate || 0);
  const total = net + tax;
  return (
    <div id="quote-print-area" style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#22302a', padding: 32, maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>{company || 'Ihr Unternehmen'}</div>
          <div style={{ fontSize: 11, color: '#66796c' }}>Angebot</div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12, color: '#66796c' }}>
          <div><strong>Angebots-Nr.:</strong> {quote.number}</div>
          <div><strong>Datum:</strong> {new Date(quote.date || Date.now()).toLocaleDateString('de-DE')}</div>
          {quote.validUntil && <div><strong>Gültig bis:</strong> {new Date(quote.validUntil).toLocaleDateString('de-DE')}</div>}
        </div>
      </div>
      <div style={{ marginBottom: 24, padding: '12px 16px', background: '#f7faf7', borderRadius: 8, border: '1px solid #e2ebe3' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{lead?.name}</div>
        <div style={{ color: '#66796c' }}>{lead?.company}</div>
        {lead?.email && <div style={{ color: '#66796c' }}>{lead.email}</div>}
      </div>
      {quote.introText && <p style={{ marginBottom: 20, lineHeight: 1.6 }}>{quote.introText}</p>}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e2ebe3', background: '#f7faf7' }}>
            {['Pos.', 'Beschreibung', 'Menge', 'Einheit', 'Einzelpreis', 'Gesamt'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Beschreibung' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#66796c', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {quote.items.map((item, i) => (
            <tr key={item.id} style={{ borderBottom: '1px solid #f0f5f0' }}>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: '#93a89a', fontSize: 12 }}>{i + 1}</td>
              <td style={{ padding: '10px 12px' }}>{item.description}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right' }}>{item.qty}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', color: '#66796c', fontSize: 12 }}>{item.unit}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmtEur(item.unitPrice)}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtEur(item.qty * item.unitPrice)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <div style={{ width: 280 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2ebe3' }}>
            <span style={{ color: '#66796c' }}>Nettobetrag</span><span style={{ fontWeight: 600 }}>{fmtEur(net)}</span>
          </div>
          {quote.taxRate > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2ebe3' }}>
              <span style={{ color: '#66796c' }}>MwSt. ({(quote.taxRate * 100).toFixed(0)}%)</span><span>{fmtEur(tax)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 18, fontWeight: 700 }}>
            <span>Gesamt</span><span style={{ color: '#0f8a53' }}>{fmtEur(total)}</span>
          </div>
        </div>
      </div>
      {quote.outroText && <p style={{ lineHeight: 1.6, color: '#66796c', fontSize: 12 }}>{quote.outroText}</p>}
    </div>
  );
}

export default function QuoteModal({ lead, currentUser, onClose, onUpdateLead, company }) {
  const [quotes, setQuotes] = useState(() => loadQuotes(lead?.id));
  const [activeId, setActiveId] = useState(null);
  const [mode, setMode] = useState('list'); // list | edit | preview

  const active = quotes.find(q => q.id === activeId);

  function persist(next) {
    setQuotes(next);
    saveQuotes(lead.id, next);
    // Sync highest accepted amount to lead
    const won = next.find(q => q.status === 'angenommen');
    if (won && onUpdateLead) {
      onUpdateLead(lead.id, {
        offerAmount: won.items.reduce((s, i) => s + i.qty * i.unitPrice, 0) * (1 + (won.taxRate || 0)),
        offerStatus: 'angenommen',
        offerDate: new Date().toISOString().slice(0, 10),
      });
    }
  }

  function createQuote() {
    const n = quotes.length + 1;
    const q = {
      id: generateId(),
      number: `ANG-${new Date().getFullYear()}-${String(n).padStart(3, '0')}`,
      date: new Date().toISOString().slice(0, 10),
      validUntil: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      status: 'entwurf',
      taxRate: 0.19,
      introText: `Sehr geehrte Damen und Herren,\n\nhiermit unterbreiten wir Ihnen folgendes Angebot:`,
      outroText: `Wir freuen uns auf Ihre Rückmeldung.\n\nMit freundlichen Grüßen\n${currentUser?.name || ''}`,
      items: [newItem()],
    };
    const next = [...quotes, q];
    persist(next);
    setActiveId(q.id);
    setMode('edit');
  }

  function updateActive(patch) {
    const next = quotes.map(q => q.id === activeId ? { ...q, ...patch } : q);
    persist(next);
  }

  function updateItem(itemId, patch) {
    updateActive({ items: active.items.map(i => i.id === itemId ? { ...i, ...patch } : i) });
  }

  function addItem() {
    updateActive({ items: [...active.items, newItem()] });
  }

  function removeItem(itemId) {
    updateActive({ items: active.items.filter(i => i.id !== itemId) });
  }

  function deleteQuote(id) {
    if (!window.confirm('Angebot löschen?')) return;
    const next = quotes.filter(q => q.id !== id);
    persist(next);
    if (activeId === id) { setActiveId(null); setMode('list'); }
  }

  function printQuote() {
    const area = document.getElementById('quote-print-area');
    if (!area) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Angebot</title><style>body{margin:0;padding:0}@media print{body{margin:0}}</style></head><body>${area.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  }

  const net   = active ? active.items.reduce((s, i) => s + i.qty * i.unitPrice, 0) : 0;
  const tax   = net * (active?.taxRate || 0);
  const total = net + tax;

  const inp = { height: 32, borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13, padding: '0 8px', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ width: 860, maxWidth: '100%', maxHeight: '92vh', background: 'var(--bg)', borderRadius: 16, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(15,30,60,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(15,138,83,0.1)', border: '1.5px solid rgba(15,138,83,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f8a53" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>Angebote</div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{lead?.name} · {lead?.company}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {mode === 'edit' && active && (
              <>
                <button onClick={() => setMode('preview')} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  Vorschau
                </button>
                <button onClick={() => setMode('list')} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>← Liste</button>
              </>
            )}
            {mode === 'preview' && (
              <>
                <button onClick={printQuote} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: 'none', background: '#0f8a53', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Drucken / PDF
                </button>
                <button onClick={() => setMode('edit')} style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>← Bearbeiten</button>
              </>
            )}
            {mode !== 'list' && <div style={{ width: 1, height: 20, background: 'var(--border)' }} />}
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* List view */}
          {mode === 'list' && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>{quotes.length} Angebot{quotes.length !== 1 ? 'e' : ''}</div>
                <button onClick={createQuote} style={{ height: 34, padding: '0 16px', borderRadius: 9, background: 'linear-gradient(135deg,#0f8a53,#159c5f)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 3px 10px rgba(29,190,116,0.3)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Neues Angebot
                </button>
              </div>
              {quotes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Noch kein Angebot</div>
                  <div style={{ fontSize: 12 }}>Erstelle das erste Angebot für {lead?.name}</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {quotes.map(q => {
                    const sm = QUOTE_STATUSES.find(s => s.key === q.status) || QUOTE_STATUSES[0];
                    const total = q.items.reduce((s, i) => s + i.qty * i.unitPrice, 0) * (1 + (q.taxRate || 0));
                    return (
                      <div key={q.id} style={{ background: 'var(--bg2)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
                        onClick={() => { setActiveId(q.id); setMode('edit'); }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-border)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{q.number}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: sm.color, background: sm.bg, padding: '1px 8px', borderRadius: 20 }}>{sm.label}</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date(q.date).toLocaleDateString('de-DE')} · {q.items.length} Position{q.items.length !== 1 ? 'en' : ''}</div>
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent)' }}>{fmtEur(total)}</div>
                        <button onClick={e => { e.stopPropagation(); deleteQuote(q.id); }}
                          style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.06)', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Edit view */}
          {mode === 'edit' && active && (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Meta row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Angebots-Nr.</div>
                  <input value={active.number} onChange={e => updateActive({ number: e.target.value })} style={{ ...inp, width: '100%' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Datum</div>
                  <input type="date" value={active.date || ''} onChange={e => updateActive({ date: e.target.value })} style={{ ...inp, width: '100%' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Gültig bis</div>
                  <input type="date" value={active.validUntil || ''} onChange={e => updateActive({ validUntil: e.target.value })} style={{ ...inp, width: '100%' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Status</div>
                  <select value={active.status} onChange={e => updateActive({ status: e.target.value })} style={{ ...inp, width: '100%', cursor: 'pointer' }}>
                    {QUOTE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Intro text */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Einleitungstext</div>
                <textarea value={active.introText || ''} onChange={e => updateActive({ introText: e.target.value })} rows={3}
                  style={{ width: '100%', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 12, padding: '8px 10px', resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.5 }} />
              </div>

              {/* Line items */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8 }}>Positionen</div>
                <div style={{ background: 'var(--bg2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 80px 110px 110px 32px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
                    {['Beschreibung', 'Menge', 'Einheit', 'Einzelpreis', 'Gesamt', ''].map(h => (
                      <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                    ))}
                  </div>
                  {active.items.map(item => (
                    <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 80px 110px 110px 32px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                      <input value={item.description} onChange={e => updateItem(item.id, { description: e.target.value })} placeholder="Leistung beschreiben…" style={{ ...inp, width: '100%', height: 30 }} />
                      <input type="number" value={item.qty} onChange={e => updateItem(item.id, { qty: Number(e.target.value) || 1 })} min="0.01" step="0.01" style={{ ...inp, width: '100%', height: 30, textAlign: 'right' }} />
                      <input value={item.unit} onChange={e => updateItem(item.id, { unit: e.target.value })} placeholder="Stk." style={{ ...inp, width: '100%', height: 30 }} />
                      <input type="number" value={item.unitPrice} onChange={e => updateItem(item.id, { unitPrice: Number(e.target.value) || 0 })} min="0" step="0.01" style={{ ...inp, width: '100%', height: 30, textAlign: 'right' }} />
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', textAlign: 'right' }}>{fmtEur(item.qty * item.unitPrice)}</div>
                      <button onClick={() => removeItem(item.id)} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                  <div style={{ padding: '8px 12px' }}>
                    <button onClick={addItem} style={{ height: 28, padding: '0 12px', borderRadius: 7, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Position hinzufügen
                    </button>
                  </div>
                </div>
              </div>

              {/* Totals + MwSt */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Steuer</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {TAX_OPTIONS.map(t => (
                      <button key={t.value} onClick={() => updateActive({ taxRate: t.value })}
                        style={{ height: 30, padding: '0 12px', borderRadius: 7, border: `1.5px solid ${active.taxRate === t.value ? 'var(--accent-border)' : 'var(--border)'}`, background: active.taxRate === t.value ? 'var(--accent-bg)' : 'var(--bg3)', color: active.taxRate === t.value ? 'var(--accent)' : 'var(--text3)', fontSize: 12, fontWeight: active.taxRate === t.value ? 700 : 400, cursor: 'pointer' }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 220 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4, fontSize: 13, color: 'var(--text2)' }}>
                    <span>Netto</span><span style={{ fontWeight: 600 }}>{fmtEur(net)}</span>
                  </div>
                  {active.taxRate > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4, fontSize: 13, color: 'var(--text2)' }}>
                      <span>MwSt. {(active.taxRate * 100).toFixed(0)}%</span><span>{fmtEur(tax)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, paddingTop: 8, borderTop: '2px solid var(--border)', fontSize: 18, fontWeight: 800, color: 'var(--accent)' }}>
                    <span>Gesamt</span><span>{fmtEur(total)}</span>
                  </div>
                </div>
              </div>

              {/* Outro */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 4 }}>Schlusstext</div>
                <textarea value={active.outroText || ''} onChange={e => updateActive({ outroText: e.target.value })} rows={3}
                  style={{ width: '100%', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 12, padding: '8px 10px', resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.5 }} />
              </div>
            </div>
          )}

          {/* Print preview */}
          {mode === 'preview' && active && (
            <QuotePrint quote={active} lead={lead} company={company} />
          )}
        </div>
      </div>
    </div>
  );
}
